import type { Editor } from '@tiptap/react'
import { useEffect, useRef } from 'react'
import { committedAcceptRefsDroppedByIncoming } from '#src/components/rich-text-editor/conditionMentionSideEffects.ts'
import { shouldReconcileEditorContent } from '#src/components/rich-text-editor/getCleanedContent.ts'
import { setContentWithoutHistory } from '#src/components/rich-text-editor/machineContentSync.ts'
import type { ReconcileOutcome } from '#src/components/rich-text-editor/noteEditorDiagnostics.ts'
import type { SuggestionPersistence } from '#src/hooks/useSuggestionPersistence.ts'

interface MountReconcileParams {
  editor: Editor | null
  isEditorReady: boolean
  // Strictly-stronger than isEditorReady: the Yjs provider reports
  // "synchronized" (initial content actually arrived), not merely
  // "synchronizing". See useIsDocSynchronized.
  isDocSynchronized: boolean
  // Number of OTHER Liveblocks presences in the room (useOthers().length).
  othersCount: number
  initialContent: string
  // The edited resource's reference when this surface persists AI suggestions
  // (Composition/{id}, CarePlan/{id}), else undefined. Distinguishes surfaces
  // whose committed accepts must be protected from ones with none.
  aiResourceRef: string | undefined
  isResourceLoaded: boolean
  // True while a write of `aiResourceRef` issued by THIS client is still in
  // flight, so the cached `initialContent` may predate it.
  hasPendingResourceWrite: boolean
  // Confine the reconcile to the moment the note opens: skip it for good when
  // another presence is there at the first eligible evaluation, or when this
  // editor has been edited, instead of waiting for either to clear.
  mountOnly: boolean
  // True once this mount has made a document change of its own (typing,
  // pasting, an insert, an undo). From then on the room is ahead of
  // `initialContent`, so any drift the compare finds is this client's work.
  hasLocalEdit: boolean
  suggestionPersistence: SuggestionPersistence
  // Called after an actual reconcile setContent so the host can bump its
  // content-reset counter (re-arms hydration paint).
  onReconciled: () => void
  // Observation-only diagnostic hook, reporting which gate below stopped this
  // evaluation or that it wrote. Undefined when the diagnostics flag is off, so
  // the whole reporting path costs one undefined check. Reported here rather
  // than reconstructed by the caller: a copy of these gate conditions would
  // drift from the real ones and silently lie about why nothing happened.
  onDecision?: (decision: ReconcileDecision) => void
}

// The gate facts the caller cannot see. Content sizes are deliberately absent —
// they cost a document walk on an effect that re-runs on every autosave, so the
// caller computes them only once a decision has cleared its throttle.
export interface ReconcileDecision {
  outcome: ReconcileOutcome
  othersCount: number
  isDocSynchronized: boolean
  isResourceLoaded: boolean
  hasPendingResourceWrite: boolean
}

// Mount-time Medplum -> Liveblocks content reconcile. Pushes Medplum
// `initialContent` into the Liveblocks room when the room's synchronized content
// has genuinely drifted from it — the recovery path for a user who edited via
// the fallback editor during a Liveblocks outage (those edits saved to Medplum
// only) and then refreshed: without this, Liveblocks would load stale room
// storage and ignore initialContent (which only seeds EMPTY documents).
export function useMountReconcile({
  editor,
  isEditorReady,
  isDocSynchronized,
  othersCount,
  initialContent,
  aiResourceRef,
  isResourceLoaded,
  hasPendingResourceWrite,
  mountOnly,
  hasLocalEdit,
  suggestionPersistence,
  onReconciled,
  onDecision,
}: MountReconcileParams): void {
  const reconciledRef = useRef(false)
  useEffect(() => {
    const report = (outcome: ReconcileOutcome): void => {
      onDecision?.({
        outcome,
        othersCount,
        isDocSynchronized,
        isResourceLoaded,
        hasPendingResourceWrite,
      })
    }
    // A spent latch reports nothing: the decision was already recorded on the
    // run that set it, and this hook keeps evaluating for the life of the mount.
    if (reconciledRef.current) {
      return
    }
    // A deferred reconcile that outlives the clinician's first edit compares
    // their unsaved typing against the last saved copy and replaces the note
    // with it. Once there is a local edit there is nothing left to recover.
    if (mountOnly && hasLocalEdit) {
      reconciledRef.current = true
      report('skipped:localEdit')
      return
    }
    if (!isEditorReady || !editor) {
      report('blocked:notReady')
      return
    }
    // Compare against the FULLY-synced room doc, never a pre-hydration empty or
    // half-synced one. `isEditorReady` also accepts the "synchronizing" state
    // where the initial content is still in flight and the doc is empty; Medplum
    // `initialContent` always "differs from" that empty doc, so an un-gated
    // reconcile would clobber the committed room content the initial sync is
    // about to deliver. Gate on the strictly-stronger "synchronized" signal —
    // the same one the reconcile sweep and hydration paint gate on — so both the
    // content-diff and the committed-accept guard below judge real room state.
    // Degraded trade-off: a session that never reaches "synchronized" will not
    // run this stale-room recovery at all — fail-safe (it never overwrites
    // against a non-synced doc), never fail-dangerous.
    if (!isDocSynchronized) {
      report('blocked:notSynchronized')
      return
    }
    // When this surface persists AI suggestions, wait for their receipts to load
    // before reconciling: the committed-accept guard below reads them, and a
    // stale Medplum copy must not win the race against a just-committed accept
    // whose receipt has not arrived from the Basic resource yet. Surfaces with no
    // AI resource (aiResourceRef undefined) have no receipts to protect and skip
    // this wait, so non-AI notes keep the original recovery behavior.
    if (aiResourceRef !== undefined && !isResourceLoaded) {
      report('blocked:resourceNotLoaded')
      return
    }
    // A write of this resource issued by THIS client may still be in flight. The
    // previous mount's unmount flush fires its PATCH as the editor tears down,
    // and a fast return remounts before the response repopulates the query
    // cache — invalidation is not eviction, so an invalidated inactive entry
    // still serves its old data on that first render. `initialContent` then
    // carries the PRE-write value while the room correctly holds the POST-write
    // one, and the diff below would push the older copy in and destroy the newer
    // content. Placed BEFORE `reconciledRef` latches so this DEFERS the
    // reconcile rather than cancelling it: the effect re-runs when the write
    // settles, by which point `initialContent` reflects it.
    if (hasPendingResourceWrite) {
      report('blocked:pendingResourceWrite')
      return
    }
    // Only reconcile when no other users are in the room. If others are
    // connected, Liveblocks room storage is actively maintained and is the
    // source of truth — pushing Medplum content in would clobber their live
    // edits. The room is per participant, so "others" includes anyone on any
    // page of the chart, and they can leave at any moment of this visit: with
    // `mountOnly` their presence here ends the reconcile rather than deferring
    // it to that moment.
    if (othersCount > 0) {
      if (mountOnly) {
        reconciledRef.current = true
        report('skipped:othersPresent')
        return
      }
      report('blocked:othersPresent')
      return
    }
    reconciledRef.current = true
    // Authority guard (data-integrity): a stale Medplum copy must NEVER reconcile
    // away a committed accept. If the incoming content drops a condition that a
    // committed accept receipt linked and the live synced doc still shows, Medplum
    // is BEHIND the room (the accept's 2.5s debounced content save was killed
    // before it flushed); overwriting would strand the chip and the reconcile
    // sweep / pre-sign drain would then read the stranded link as an orphan and
    // unlink Encounter.diagnosis — the exact clinical data loss this seam causes.
    // Skipping is the deliberate bias for a clinical surface. The one case it
    // trades away: a chip legitimately deleted via a Medplum-only path (fallback
    // editor during a Liveblocks outage) while a committed-accept receipt still
    // exists — that deletion will not propagate to a stale room until the receipt
    // situation changes, because incoming always lacks the id while the room
    // always has it. Recoverable visual staleness is strictly preferable to a
    // silent clinical unlink, so do NOT "fix" this back into an unconditional
    // overwrite.
    const receipts = suggestionPersistence
      .getAllSuggestions()
      .map((s) => suggestionPersistence.getReceipt(s.id))
    if (
      committedAcceptRefsDroppedByIncoming(editor, initialContent, receipts)
        .length > 0
    ) {
      report('blocked:committedAccept')
      return
    }
    // Canonicalize BOTH sides through the editor schema and compare in HTML space
    // (see shouldReconcileEditorContent). This avoids spurious resets on every
    // solo mount: the live editor parse drops schema-undeclared attrs (vestigial
    // data-choice-id), preserves block-edge whitespace, and appends a TrailingNode
    // empty <p>, none of which represent real drift versus the stored
    // initialContent. The compare fires only on genuine text/structure/declared-
    // attr drift (e.g. fallback-editor edits saved to Medplum during an outage).
    if (shouldReconcileEditorContent(editor, initialContent)) {
      // addToHistory:false (setContentWithoutHistory): this Medplum->Liveblocks
      // reconcile is a machine content-sync, never a user edit. Without the
      // suppression y-prosemirror captures it as undo item #1 on the freshly
      // mounted UndoManager, and a post-reload Cmd+Z pops it — which the
      // accepted->pending reconciler misreads as an undo of a committed accept,
      // flipping it back and clearing its receipt.createdRefs server-side.
      // Reported BEFORE the write so the content sizes the caller reads still
      // describe the drift that justified it, not the post-write document.
      report('latched:wrote')
      setContentWithoutHistory(editor, initialContent, {
        parseOptions: { preserveWhitespace: true },
      })
      onReconciled()
      return
    }
    report('latched:noDrift')
  }, [
    isEditorReady,
    editor,
    isDocSynchronized,
    othersCount,
    initialContent,
    aiResourceRef,
    isResourceLoaded,
    hasPendingResourceWrite,
    mountOnly,
    hasLocalEdit,
    suggestionPersistence,
    onReconciled,
    onDecision,
  ])
}
